import React, { useMemo, useRef } from "react";
import { getUIColorPalette } from "../../../theme/ColorPalette";
import { formatStopName } from "../../../tools/stopNameFormatter";
import { type PopupContent } from "../vehicleSelection/vehicleSelection.types";
import { useVehicleIslandDelayStatusCompact } from "./useVehicleIslandDelayStatusCompact.hook";
import {
    formatVehicleIslandCenterSubline,
    getVehicleColorRgb,
    getVehicleColorWithOpacity,
    getVehicleInfoTextColor,
    resolveEffectiveVehicleDelaySec,
    resolveVehicleDelayBadgePresentation,
    VEHICLE_INFO_BACKGROUND_OPACITY,
    VEHICLE_INFO_BADGE_MIN_WIDTH_PX,
    VEHICLE_INFO_HEADER_OPACITY,
    VEHICLE_INFO_TRIP_HEADING_MAX_WIDTH_PX,
    VEHICLE_ISLAND_CENTER_SUBLINE_TEXT_CLASS,
    VEHICLE_ISLAND_HEADSIGN_TEXT_CLASS,
    VEHICLE_ISLAND_ROW_CLASS_NAME,
    VEHICLE_ISLAND_TEXT_COLUMN_CLASS_NAME,
    VEHICLE_ISLAND_TRAILING_CLUSTER_CLASS_NAME,
    vehicleIslandSideControlStyle,
} from "./vehicleInfoChrome.tools";
import VehicleIslandDelayBadge from "./VehicleIslandDelayBadge";

/** Text colors for high-contrast vehicle chrome */
const TEXT_COLOR_BLACK = "#000000";
const TEXT_COLOR_WHITE = "#FFFFFF";

interface VehicleInfoChromeProps {

    /** Vehicle popup / island content */
    "content": PopupContent | undefined;

    /** Whether dark UI palette is active */
    "isDarkUi": boolean;

    /** Optional extra class names on the outer shell */
    "className"?: string;

    /** Optional inline styles on the outer shell (width/height) */
    "style"?: React.CSSProperties;

    /** Horizontal layout of badge + trip headsign. Defaults to `start`. */
    "contentAlign"?: "start" | "center" | "island";

    /**
     * Optional trailing control for island layout (e.g. follow toggle).
     * Replaces the invisible mirror badge used for optical centering.
     */
    "trailingAction"?: React.ReactNode;

    /** Fired when the user activates the main island content (not the trailing action). */
    "onIslandActivate"?: () => void;

    /** Accessible label when {@link onIslandActivate} is set. */
    "islandActivateAriaLabel"?: string;
}

/**
 * Shared rounded vehicle info chrome (badge + trip headsign or error states).
 * Used by the map popup and Dynamic Island vehicle variant.
 *
 * @param props Component props.
 * @returns Rendered vehicle info chrome or null when content is absent.
 */
const VehicleInfoChrome: React.FC<VehicleInfoChromeProps> = ( {
    content,
    isDarkUi,
    className = "",
    style,
    contentAlign = "start",
    trailingAction,
    onIslandActivate,
    islandActivateAriaLabel,
} ) => {
    const palette = getUIColorPalette( isDarkUi );
    const rowRef = useRef<HTMLDivElement>( null );
    const headsignRef = useRef<HTMLDivElement>( null );
    const lineBadgeRef = useRef<HTMLDivElement>( null );
    const formattedHeadsign = content !== undefined &&
      content.trip_headsign !== undefined &&
      content.trip_headsign !== ""
        ? formatStopName( content.trip_headsign )
        : null;
    const hasLineBadge = ( content?.label ?? "" ) !== "";
    const delaySec = content !== undefined ? resolveEffectiveVehicleDelaySec( content ) : undefined;
    const { useCompactStatusLine } = useVehicleIslandDelayStatusCompact( {
        delaySec,
        "formattedHeadsign": contentAlign === "island" ? formattedHeadsign : null,
        headsignRef,
        rowRef,
    } );
    const delayBadgePresentation = useMemo(
        () => {
            if ( content === undefined ) {
                return {
                    "label":                null,
                    "statusLine":           null,
                    "valueLine":            null,
                    "badgeBackgroundColor": null,
                    "badgeBorderColor":     null,
                    "badgeTextColor":       null,
                };
            }

            return resolveVehicleDelayBadgePresentation( content, isDarkUi, { "useCompactStatusLine": contentAlign === "island" ? useCompactStatusLine : true } );
        },
        [ content, contentAlign, isDarkUi, useCompactStatusLine ],
    );

    if ( content === undefined ) {
        return null;
    }

    const isError = content.stop_name === "Erreur lors de la récupération des données";
    const noTripFound = content.stop_name === "Aucun trajet trouvé";
    const textColor = getVehicleInfoTextColor( content.color ) === "black"
        ? TEXT_COLOR_BLACK
        : TEXT_COLOR_WHITE;
    const shellBackground = getVehicleColorWithOpacity( content.color, VEHICLE_INFO_BACKGROUND_OPACITY );
    const rowJustifyClass = contentAlign === "center" ? "justify-center" : "justify-start";

    /**
     * Renders the island line badge with the same footprint as the follow toggle.
     *
     * @returns Line badge node or null when label is empty.
     */
    const renderIslandLineBadge = (): React.ReactNode => {
        if ( !hasLineBadge ) {
            return null;
        }

        return (
            <div
                className="flex shrink-0 items-center justify-center self-center overflow-hidden rounded-full px-3 font-bold text-sm text-center leading-none"
                ref={lineBadgeRef}
                style={{
                    "backgroundColor": getVehicleColorRgb( content.color ),
                    "color":           textColor,
                    ...vehicleIslandSideControlStyle,
                }}
            >
                {content.label}
            </div>
        );
    };

    /**
     * Renders the line-number pill. Factory avoids reusing one React element in two tree locations.
     *
     * @returns Line badge node or null when label is empty.
     */
    const renderLineBadge = (): React.ReactNode => {
        if ( !hasLineBadge ) {
            return null;
        }

        return (
            <div
                className="px-3 py-[1px] rounded-full font-bold text-sm shrink-0 text-center"
                style={{
                    "backgroundColor": getVehicleColorRgb( content.color ),
                    "color":           textColor,
                    "minWidth":        `${ VEHICLE_INFO_BADGE_MIN_WIDTH_PX }px`,
                }}
            >
                {content.label}
            </div>
        );
    };

    const tripHeadsign = formattedHeadsign !== null
        ? (
            <div
                className={contentAlign === "island" ? VEHICLE_ISLAND_HEADSIGN_TEXT_CLASS : "font-bold text-sm whitespace-nowrap overflow-hidden truncate"}
                ref={contentAlign === "island" ? headsignRef : undefined}
                style={{ "maxWidth": contentAlign === "start" ? `${ VEHICLE_INFO_TRIP_HEADING_MAX_WIDTH_PX }px` : undefined }}
            >
                {formattedHeadsign}
            </div>
        )
        : null;

    const centerSublineText = formatVehicleIslandCenterSubline(
        content.route_type_france,
        content.trip_headsign,
        content.trip_headsign_original,
    );
    const centerSubline = centerSublineText !== null && contentAlign === "island"
        ? (
            <div className={VEHICLE_ISLAND_CENTER_SUBLINE_TEXT_CLASS}>
                {centerSublineText}
            </div>
        )
        : null;
    const hasTextColumn = tripHeadsign !== null || centerSubline !== null;
    const hasTrailingCluster = contentAlign === "island" &&
      ( delayBadgePresentation.statusLine !== null || trailingAction !== undefined );
    const islandTextColumn = hasTextColumn
        ? (
            <div className={VEHICLE_ISLAND_TEXT_COLUMN_CLASS_NAME}>
                {tripHeadsign}
                {centerSubline}
            </div>
        )
        : null;

    const handleIslandKeyDown = ( event: React.KeyboardEvent<HTMLDivElement> ): void => {
        if ( onIslandActivate === undefined ) {
            return;
        }
        if ( event.key === "Enter" || event.key === " " ) {
            event.preventDefault();
            onIslandActivate();
        }
    };

    return (
        <div
            className={`rounded-full shadow-lg overflow-hidden backdrop-blur-sm border-2 flex flex-col ${ className }`}
            style={{
                "background":  shellBackground,
                "borderColor": getVehicleColorWithOpacity( content.color, VEHICLE_INFO_HEADER_OPACITY ),
                ...style,
            }}
        >
            {isError || noTripFound
                ? (
                    <div className="p-4 h-full overflow-hidden">
                        <div
                            className={`flex items-center gap-2 ${ rowJustifyClass }`}
                            style={{ "color": palette.overlayTextPrimary }}
                        >
                            <svg
                                className="w-5 h-5"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                            <p className="text-sm font-medium">
                                {isError
                                    ? "Erreur lors de la récupération des données"
                                    : "Aucun trajet trouvé"}
                            </p>
                        </div>
                    </div>
                )
                : (
                    <div
                        className={`px-2 h-full flex items-center w-full ${
                            contentAlign === "island" ? "" : rowJustifyClass
                        }`}
                        style={{
                            "backgroundColor": getVehicleColorWithOpacity( content.color, VEHICLE_INFO_HEADER_OPACITY ),
                            "color":           textColor,
                        }}
                    >
                        {contentAlign === "island"
                            ? (
                                <div
                                    aria-label={onIslandActivate !== undefined ? islandActivateAriaLabel : undefined}
                                    className={`relative ${ VEHICLE_ISLAND_ROW_CLASS_NAME } ${
                                        onIslandActivate !== undefined ? "cursor-pointer" : ""
                                    }`}
                                    onClick={onIslandActivate}
                                    onKeyDown={handleIslandKeyDown}
                                    ref={rowRef}
                                    role={onIslandActivate !== undefined ? "button" : undefined}
                                    tabIndex={onIslandActivate !== undefined ? 0 : undefined}
                                >
                                    {renderIslandLineBadge()}
                                    {islandTextColumn}
                                    {hasTrailingCluster
                                        ? (
                                            <div className={VEHICLE_ISLAND_TRAILING_CLUSTER_CLASS_NAME}>
                                                <VehicleIslandDelayBadge presentation={delayBadgePresentation} />
                                                {trailingAction}
                                            </div>
                                        )
                                        : hasLineBadge
                                            ? (
                                                <div
                                                    aria-hidden="true"
                                                    className="invisible shrink-0 self-center pointer-events-none"
                                                    style={vehicleIslandSideControlStyle}
                                                />
                                            )
                                            : null}
                                </div>
                            )
                            : (
                                <div className="flex items-center gap-1 min-w-0">
                                    {renderLineBadge()}
                                    {tripHeadsign}
                                </div>
                            )}
                    </div>
                )}
        </div>
    );
};

export default VehicleInfoChrome;
